// src/auth/AuthContext.jsx
import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [inProgress, setInProgress] = useState('none');
  const navigate = useNavigate();

  // Initialize authentication state
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        console.log('🔄 Initializing authentication...');

        // First, trust server session cookie if present
        try {
          // Use cache: 'no-store' to avoid 304 responses during initialization
          // We need fresh user data, not cached responses
          const meResp = await fetch('/api/me', { 
            credentials: 'include',
            cache: 'no-store'
          });
          if (meResp.ok || meResp.status === 304) {
            try {
              const data = await meResp.json();
              if (data?.user) {
                const u = data.user;
                setIsAuthenticated(true);
                setIsAdmin((u.role || '').toLowerCase() === 'admin');
                setUser({
                  name: u.name || u.email,
                  mail: u.email,
                  role: u.role,
                  userType: u.userType,
                  canSeePricing: u.canSeePricing,
                  companyName: u.companyName || '',
                  companyIabCategory: u.companyIabCategory || '',
                  contactPerson: u.contactPerson || '',
                  contactEmail: u.contactEmail || '',
                  contactPhone: u.contactPhone || '',
                  venueName: u.venueName || '',
                  publisherName: u.publisherName || '',
                  venueId: u.venueId,
                  venueUid: u.venueUid,
                  isVenue: u.isVenue,
                  isAffiliate: u.isAffiliate,
                  affiliatePmps: u.affiliatePmps || [],
                  venues: u.venues || [],
                  hasMultipleVenues: u.hasMultipleVenues || false,
                });
                console.log('✅ Auth via server cookie:', { email: u.email, role: u.role });
                // If server cookie exists, prefer it and skip any MSAL redirect handling here
                setLoading(false);
                return;
              }
            } catch (jsonError) {
              // If 304, response body might be empty, but user is still authenticated
              if (meResp.status === 304) {
                console.log('✅ Auth via server cookie (304): User authenticated but no user data available');
                // For 304 without data, user is authenticated but we don't have details
                // Make a fresh request without cache to get user data
                try {
                  const freshResp = await fetch('/api/me', { 
                    credentials: 'include',
                    headers: { 'Cache-Control': 'no-cache' }
                  });
                  if (freshResp.ok) {
                    const freshData = await freshResp.json();
                    if (freshData?.user) {
                      const u = freshData.user;
                      setIsAuthenticated(true);
                      setIsAdmin((u.role || '').toLowerCase() === 'admin');
                      setUser({
                        name: u.name || u.email,
                        mail: u.email,
                        role: u.role,
                        userType: u.userType,
                        canSeePricing: u.canSeePricing,
                        companyName: u.companyName || '',
                        companyIabCategory: u.companyIabCategory || '',
                        contactPerson: u.contactPerson || '',
                        contactEmail: u.contactEmail || '',
                        contactPhone: u.contactPhone || '',
                        venueName: u.venueName || '',
                        publisherName: u.publisherName || '',
                        venueId: u.venueId,
                        venueUid: u.venueUid,
                        isVenue: u.isVenue,
                        venues: u.venues || [],
                        hasMultipleVenues: u.hasMultipleVenues || false,
                      });
                      console.log('✅ Auth via server cookie (fresh):', { email: u.email, role: u.role });
                      setLoading(false);
                      return;
                    }
                  }
                } catch (freshError) {
                  console.warn('⚠️ Failed to get fresh user data:', freshError);
                }
                // If fresh request also fails, at least mark as authenticated
                setIsAuthenticated(true);
                setLoading(false);
                return;
              }
              // For other JSON parse errors, log but continue
              console.warn('⚠️ Failed to parse /api/me response:', jsonError);
            }
          }
        } catch (e) {
          // ignore, fallback to MSAL
          console.log('ℹ️ /api/me fetch error (non-fatal):', e.message);
        }

        // If no server cookie, we do not attempt client-side MSAL login here.
        // Admins use backend SSO which will set the cookie and then the next /api/me will succeed.
        console.log('ℹ️ No server session cookie found; awaiting user action to log in.');
      } catch (error) {
        console.error('❌ Auth initialization error:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();

    // No event listeners are required since we do not manage MSAL client login in the SPA
    return () => {};
  // eslint-disable-next-line react-hooks/exhaustive-deps -- run only on mount; re-running on venue selection caused modal to reappear
  }, []);

  // Note: We no longer call Microsoft Graph from the client to determine admin status.
  // The server sets role/userType/canSeePricing in the cookie and /api/me returns it.

  // FIXED: Use redirect with proper interaction status tracking
  const login = async () => {
    try {
      setLoading(true);
      setError(null);
  setInProgress('Login');
      
      console.log('🔄 Starting backend SSO login redirect...');
      window.location.href = '/api/auth/azure/login?next=' + encodeURIComponent(window.location.pathname || '/');
      
    } catch (error) {
      console.error('❌ Login error:', error);
      setError(error.message || 'Login failed');
  setInProgress('none');
      setLoading(false);
    }
  };

  // Prefer app logout (clear server cookie) without forcing Microsoft sign-out
  const logout = async () => {
    try {
      setLoading(true);
      setError(null);
  setInProgress('Logout');

      // Clear app session cookie
      try {
        await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
      } catch {}

      // Do NOT trigger MSAL logout by default to avoid AAD prompts
      setIsAuthenticated(false);
      setIsAdmin(false);
      setUser(null);
    setInProgress('none');
    setLoading(false);
    navigate('/login', { replace: true });
      
    } catch (error) {
      console.error('❌ Logout error:', error);
      setError(error.message || 'Logout failed');
  	setInProgress('none');
  	      setLoading(false);
    }
  };

  const getAccessToken = async () => {
    throw new Error('Client-side access tokens are not used. The server handles Microsoft Graph calls.');
  };

  const clearError = () => {
    setError(null);
  };

  const selectVenue = async (venueUid) => {
    try {
      setInProgress('SelectingVenue');
      const response = await fetch('/api/select-venue', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ venueUid }),
      });

      if (response.ok) {
        // Refresh user data to get updated venue information (no-store to avoid stale 304)
        const meResp = await fetch('/api/me', { credentials: 'include', cache: 'no-store' });
        if (meResp.ok) {
          const data = await meResp.json();
          const u = data.user;
          setUser({
            name: u.name || u.email,
            mail: u.email,
            role: u.role,
            userType: u.userType,
            canSeePricing: u.canSeePricing,
            companyName: u.companyName || '',
            companyIabCategory: u.companyIabCategory || '',
            contactPerson: u.contactPerson || '',
            contactEmail: u.contactEmail || '',
            contactPhone: u.contactPhone || '',
            venueName: u.venueName || '',
            publisherName: u.publisherName || '',
            venueId: u.venueId,
            venueUid: u.venueUid,
            isVenue: u.isVenue,
            venues: u.venues || [],
            hasMultipleVenues: u.hasMultipleVenues || false,
          });
        }
      } else {
        throw new Error('Failed to select venue');
      }
    } catch (error) {
      console.error('❌ Venue selection error:', error);
      setError(error.message);
    } finally {
      setInProgress('none');
    }
  };

  const value = {
    isAuthenticated,
    isAdmin,
    user,
    loading,
    error,
    inProgress,
    login,
    logout,
    getAccessToken,
    clearError,
    selectVenue,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};